import React from "react";
import { CmpMemberLifecycleStatusEnum, CmpMemberStatusEnum } from "../../../graphql/types";

/**
 * Monday 13389365001 (S9). CMP Dashboard (24) mmAutoRenewOn (CD:14412): the
 * warning shows when the chamber allows auto-renewal and the member chose it,
 * except for a Pending member or a renewal that is already cancelled.
 */
export const shouldWarnAutoRenewOnDelete = ({
  autoRenewEnabled,
  lifecycleStatus,
  memberStatus,
}: {
  autoRenewEnabled?: boolean | null;
  lifecycleStatus?: CmpMemberLifecycleStatusEnum | null;
  memberStatus?: CmpMemberStatusEnum | null;
}) =>
  !!autoRenewEnabled &&
  lifecycleStatus !== CmpMemberLifecycleStatusEnum.Pending &&
  memberStatus !== CmpMemberStatusEnum.Pending &&
  memberStatus !== CmpMemberStatusEnum.Cancelling;

/**
 * The Delete Member pop-up message, the same in Update Member and the Manage
 * Members row (CMP Dashboard (24) CD:16081 and CD:16996; CD:15759 for the
 * status model, Q37). Chambers without the status model keep the short text.
 */
export const DeleteMemberMessage = ({ company, statusModel }: { company: string; statusModel: boolean }) =>
  statusModel ? (
    <>Are you sure you want to delete <strong>{company}</strong>? The membership will be cancelled immediately. All member privileges, directory listing and Member Dashboard access will be removed, and no renewal invoice will be sent. The member will show as Deleted, and member data can be recalled for 365 days when creating a new member. After 365 days the member moves to Past Member.</>
  ) : (
    <>Are you sure you want to delete <strong>{company}</strong>? All information will be stored as a Past Member.</>
  );

/** CMP Dashboard (24) CD:7164: the Delete Member pop-up auto-renewal warning. */
export const AutoRenewDeleteWarning = ({ company }: { company: string }) => (
  <div
    role="note"
    className="mt-[14px] flex w-full max-w-[420px] items-start gap-[10px] rounded-[8px] border border-[#F0202E] bg-[#FFF1F1] px-[12px] py-[10px] text-left"
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#F0202E"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-[1px] h-[18px] w-[18px] shrink-0"
      aria-hidden="true"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
    <span className="text-[13px] leading-[1.5] text-[#0A0A0A]">
      <strong className="font-bold text-[#F0202E]">Credit Card Auto-Renewal Enabled.</strong>{" "}
      {company} has membership auto-renewal enabled and their card on file is set to be charged on the renewal date. Would you still like to delete this member?
    </span>
  </div>
);

/**
 * Not in design (Q14, Moeen Oct 28 2026: the refusal reason shows). The
 * backend archive readiness reason under a grey Delete Member button, in the
 * design inline error style (13px, #F21B1B).
 */
export const DeleteMemberBlockedReason = ({ reason }: { reason?: string | null }) =>
  reason ? (
    <span className="text-[13px] leading-[1.5] text-[#F21B1B]" data-testid="delete-member-blocked-reason">
      {reason}
    </span>
  ) : null;
